import React, { useState, useEffect } from 'react';
import { ref, onValue, remove, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { realtimeDb } from '../firebase';
import { Eye, Trash2, RefreshCw, AlertTriangle, Users, Clock, Trophy, X, Gamepad2, Search, Filter } from 'lucide-react';

interface Game {
  id: string;
  level: number;
  player1: string;
  player2?: string;
  status: 'waiting' | 'playing' | 'completed';
  currentTurn: string;
  ballsRemaining: number;
  scores: { [key: string]: number };
  gameData?: any;
  createdAt?: number;
  gamePhase?: string;
  eightBallPocketed?: boolean;
  foul?: boolean;
  player1Balls?: number[];
  player2Balls?: number[];
}

interface GameDetails {
  game: Game;
  player1Info?: any;
  player2Info?: any;
  gameDuration?: number;
  lastActivity?: number;
}

const GamesManagement: React.FC = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedGame, setSelectedGame] = useState<GameDetails | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [filter, setFilter] = useState<'all' | 'waiting' | 'playing' | 'completed'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const auth = getAuth();

  useEffect(() => {
    if (!auth.currentUser) return;

    const gamesRef = ref(realtimeDb, 'games');

    const unsubscribe = onValue(gamesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const gamesArray = Object.entries(data).map(([id, game]: [string, any]) => ({
          id,
          ...game,
          createdAt: game.createdAt || Date.now()
        }));
        setGames(gamesArray);
      } else {
        setGames([]);
      }
      setLoading(false);
    }, (error) => {
      console.error('Error fetching games:', error);
      
      // Mock adatok fejlesztéshez
      const mockGames: Game[] = [
        {
          id: 'mock-game-1',
          level: 1,
          player1: 'user1@example.com',
          player2: 'user2@example.com',
          status: 'playing',
          currentTurn: 'user1@example.com',
          ballsRemaining: 6,
          scores: { 'user1@example.com': 15, 'user2@example.com': 12 },
          createdAt: Date.now() - 300000, // 5 perce
          gamePhase: 'solids',
          eightBallPocketed: false,
          foul: false,
          player1Balls: [1, 2, 3],
          player2Balls: [9, 10, 11]
        },
        {
          id: 'mock-game-2',
          level: 2,
          player1: 'user3@example.com',
          status: 'waiting',
          currentTurn: 'user3@example.com',
          ballsRemaining: 8,
          scores: { 'user3@example.com': 0 },
          createdAt: Date.now() - 60000, // 1 perce
          gamePhase: 'break',
          eightBallPocketed: false,
          foul: false,
          player1Balls: [],
          player2Balls: []
        },
        {
          id: 'mock-game-3',
          level: 1,
          player1: 'user4@example.com',
          player2: 'user5@example.com',
          status: 'completed',
          currentTurn: 'user5@example.com',
          ballsRemaining: 0,
          scores: { 'user4@example.com': 25, 'user5@example.com': 30 },
          createdAt: Date.now() - 1800000, // 30 perce
          gamePhase: 'eight',
          eightBallPocketed: true,
          foul: false,
          player1Balls: [1, 2, 3, 4, 5, 6, 7],
          player2Balls: [9, 10, 11, 12, 13, 14, 15]
        }
      ];
      
      setGames(mockGames);
      setError('Firebase jogosultsági hiba - mock adatok használata');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth.currentUser]);

  const deleteGame = async (gameId: string) => {
    try {
      await remove(ref(realtimeDb, `games/${gameId}`));
      console.log(`Game ${gameId} deleted successfully`);
    } catch (error) {
      console.error('Error deleting game:', error);
      setError('Hiba történt a játék törlése során');
    }
  };

  const cleanupOldGames = async () => {
    try {
      const now = Date.now();
      const oneHourAgo = now - (60 * 60 * 1000);
      const twoHoursAgo = now - (2 * 60 * 60 * 1000);

      let deletedCount = 0;
      const gamesToDelete = games.filter(game => {
        return (
          (game.status === 'waiting' && game.createdAt && game.createdAt < oneHourAgo) ||
          (game.status === 'playing' && game.createdAt && game.createdAt < twoHoursAgo) ||
          (!game.createdAt && game.status === 'waiting') ||
          (typeof game.level === 'undefined' || typeof game.status === 'undefined' || !game.player1)
        );
      });

      for (const game of gamesToDelete) {
        await remove(ref(realtimeDb, `games/${game.id}`));
        deletedCount++;
      }

      console.log(`Cleanup completed. Deleted ${deletedCount} old/corrupted games.`);
      alert(`${deletedCount} régi/sérült játék törölve!`);
    } catch (error) {
      console.error('Error during cleanup:', error);
      setError('Hiba történt a cleanup során');
    }
  };

  const deleteAllGames = async () => {
    if (window.confirm('Biztosan törölni szeretnéd az összes játékot?')) {
      try {
        await remove(ref(realtimeDb, 'games'));
        console.log('All games deleted successfully');
        alert('Az összes játék törölve!');
      } catch (error) {
        console.error('Error deleting all games:', error);
        setError('Hiba történt az összes játék törlése során');
      }
    }
  };

  const getGameDetails = async (game: Game) => {
    try {
      const details: GameDetails = { game };
      
      // Játékos információk lekérése
      if (game.player1) {
        const player1Ref = ref(realtimeDb, `users/${game.player1}`);
        const player1Snapshot = await get(player1Ref);
        if (player1Snapshot.exists()) {
          details.player1Info = player1Snapshot.val();
        }
      }
      
      if (game.player2) {
        const player2Ref = ref(realtimeDb, `users/${game.player2}`);
        const player2Snapshot = await get(player2Ref);
        if (player2Snapshot.exists()) {
          details.player2Info = player2Snapshot.val();
        }
      }
      
      // Játék időtartam számítása
      if (game.createdAt) {
        details.gameDuration = Date.now() - game.createdAt;
      }
      
      setSelectedGame(details);
      setShowDetails(true);
    } catch (error) {
      console.error('Error getting game details:', error);
      setError('Hiba történt a játék részleteinek lekérése során');
    }
  };

  const filteredGames = games.filter(game => {
    const matchesFilter = filter === 'all' || game.status === filter;
    const matchesSearch = searchTerm === '' || 
      game.player1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      game.player2?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      game.id.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'waiting':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'playing':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'completed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('hu-HU');
  };

  const formatDuration = (milliseconds: number) => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Játékok Kezelése</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            A Game Winners játékok kezelése és monitorozása
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-2">
          <button onClick={cleanupOldGames} className="btn-secondary">
            <RefreshCw className="mr-2 h-4 w-4" />
            Cleanup
          </button>
          <button onClick={deleteAllGames} className="btn-danger">
            <Trash2 className="mr-2 h-4 w-4" />
            Összes törlése
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="card border-red-200 dark:border-red-700">
          <div className="card-body">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700 dark:text-red-300">{error}</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Keresés játékos vagy játék ID alapján..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10"
              />
            </div>

            {/* Filter */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter className="h-5 w-5 text-gray-400" />
              </div>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="form-input pl-10"
              >
                <option value="all">Összes játék</option>
                <option value="waiting">Várakozó</option>
                <option value="playing">Játszik</option>
                <option value="completed">Befejezett</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Games Cards (Mobile) */}
      <div className="lg:hidden">
        <div className="space-y-4">
          {filteredGames.map((game) => (
            <div key={game.id} className="card">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <Gamepad2 className="h-5 w-5 text-blue-500" />
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                        Játék #{game.id.slice(-6)}
                      </h3>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(game.status)}`}>
                        {game.status === 'waiting' ? 'Várakozó' : 
                         game.status === 'playing' ? 'Játszik' : 'Befejezett'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {game.player1} {game.player2 ? `vs ${game.player2}` : '(egyedül)'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {game.createdAt ? formatDate(game.createdAt) : 'Ismeretlen időpont'}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => getGameDetails(game)}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 p-1 transition-colors duration-200"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => deleteGame(game.id)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1 transition-colors duration-200"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Games Table (Desktop) */}
      <div className="hidden lg:block">
        <div className="card">
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Játék
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Játékosok
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Állapot
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Pontszám
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Időpont
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Műveletek
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredGames.map((game) => (
                  <tr key={game.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Gamepad2 className="h-5 w-5 text-blue-500 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            #{game.id.slice(-6)}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Szint {game.level}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {game.player1}
                      </div>
                      {game.player2 && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          vs {game.player2}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(game.status)}`}>
                        {game.status === 'waiting' ? 'Várakozó' : 
                         game.status === 'playing' ? 'Játszik' : 'Befejezett'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {Object.values(game.scores || {}).reduce((a, b) => a + b, 0)} pont
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {game.createdAt ? formatDate(game.createdAt) : 'Ismeretlen'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => getGameDetails(game)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 transition-colors duration-200"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteGame(game.id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 transition-colors duration-200"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Game Details Modal */}
      {showDetails && selectedGame && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDetails(false)}></div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Játék Részletek
                  </h3>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Játék ID</h4>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedGame.game.id}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Játékosok</h4>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedGame.game.player1} {selectedGame.game.player2 ? `vs ${selectedGame.game.player2}` : '(egyedül)'}
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Állapot</h4>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedGame.game.status)}`}>
                      {selectedGame.game.status === 'waiting' ? 'Várakozó' : 
                       selectedGame.game.status === 'playing' ? 'Játszik' : 'Befejezett'}
                    </span>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Pontszámok</h4>
                    <div className="text-sm text-gray-900 dark:text-white">
                      {Object.entries(selectedGame.game.scores || {}).map(([player, score]) => (
                        <div key={player}>{player}: {score} pont</div>
                      ))}
                    </div>
                  </div>
                  
                  {selectedGame.gameDuration && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Játék időtartam</h4>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {formatDuration(selectedGame.gameDuration)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setShowDetails(false)}
                  className="btn-secondary w-full sm:w-auto sm:ml-3"
                >
                  Bezárás
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GamesManagement; 